import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { Routes, Route, Link, useLocation, Navigate, BrowserRouter as Router } from 'react-router-dom';
import { usePostHog } from '@posthog/react';

// Auth Context
import { AuthProvider, useAuth } from './MosqifyAuthContext'; // Using Mosqify API
import { MosqueProvider } from './MosqueContext'; // Mosque Context for selected mosque
import { ThemeProvider } from './contexts/ThemeContext';
import MosqifyApiLoginScreen from './components/Auth/MosqifyApiLoginScreen'; // Using Mosqify API

// Vercel speed tracking
import { SpeedInsights } from "@vercel/speed-insights/react";

// Flaticon CSS Imports
import '@flaticon/flaticon-uicons/css/solid/rounded.css';
import '@flaticon/flaticon-uicons/css/bold/rounded.css';
import '@flaticon/flaticon-uicons/css/all/all.css';
import '@flaticon/flaticon-uicons/css/regular/rounded.css';
import '@flaticon/flaticon-uicons/css/regular/straight.css';

// Import PetaMasjid component
const PetaMasjid = lazy(() => import('./components/PetaMasjid'));

// Import screen components
const BerandaScreen = lazy(() => import('./components/Beranda/BerandaScreen'));
const LainnyaMenuScreen = lazy(() => import('./components/Beranda/LainnyaMenuScreen'));
const QuranFeature = lazy(() => import('./components/Quran/QuranFeature'));
const BeritaScreen = lazy(() => import('./components/Berita/BeritaScreen'));
const ZakatCalculatorScreen = lazy(() => import('./components/Zakat/ZakatCalculatorScreen')); // Impor Kalkulator Zakat
const ZikirScreen = lazy(() => import('./components/Zikir/ZikirScreen'));
const DoaFeature = lazy(() => import('./components/Doa/DoaFeature'));
const KiblaScreen = lazy(() => import('./components/Kibla/KiblaScreen'));
const JadwalSolatScreen = lazy(() => import('./components/JadwalSolat/JadwalSolatScreen'));
const ProfileScreen = lazy(() => import('./components/Profile/ProfileScreen'));
// Import Profile sub-screens
const AccountInfoScreen = lazy(() => import('./components/Profile/AccountInfoScreen'));
const InboxScreen = lazy(() => import('./components/Profile/InboxScreen'));
const DonationHistoryScreen = lazy(() => import('./components/Profile/DonationHistoryScreen'));
const SettingsScreen = lazy(() => import('./components/Profile/SettingsScreen'));
const HelpCenterScreen = lazy(() => import('./components/Profile/HelpCenterScreen'));
const ShippingStatusScreen = lazy(() => import('./components/Profile/ShippingStatusScreen')); // Impor baru

const DonasiScreen = lazy(() => import('./components/Donasi/DonasiScreen'));
const EventScreen = lazy(() => import('./components/Event/EventScreen'));
const EventDetailScreen = lazy(() => import('./components/Event/EventDetailScreen'));
const EventRegistrationScreen = lazy(() => import('./components/Event/EventRegistrationScreen'));
const EventPaymentScreen = lazy(() => import('./components/Event/EventPaymentScreen'));
const TicketHistoryScreen = lazy(() => import('./components/Event/TicketHistoryScreen'));
const MosqueProfileFromQR = lazy(() => import('./components/DailyJamaah/MosqueProfileFromQR'));
const TvDisplayScreen = lazy(() => import('./components/TvDisplay/TvDisplayScreen'));
const MisiHarianScreen = lazy(() => import('./pages/MisiHarianScreen'));
const CashFlowScreen = lazy(() => import('./components/CashFlow/CashFlowScreen'));
const AktifitasScreen = lazy(() => import('./components/Aktifitas/AktifitasScreen'));
const DonaturScreen = lazy(() => import('./components/Donatur/DonaturScreen'));

// Legal Pages
const TermsOfServiceScreen = lazy(() => import('./components/Legal/TermsOfServiceScreen'));
const PrivacyPolicyScreen = lazy(() => import('./components/Legal/PrivacyPolicyScreen'));

// Auth Pages
const ForgotPasswordScreen = lazy(() => import('./components/Auth/ForgotPasswordScreen'));
const EmailVerificationScreen = lazy(() => import('./components/Auth/EmailVerificationScreen'));
const TestEmailVerification = lazy(() => import('./components/Auth/TestEmailVerification'));
const GoogleOAuthCallback = lazy(() => import('./components/Auth/GoogleOAuthCallback'));

// Market Feature Components
const MarketScreen = lazy(() => import('./components/Market/MarketScreen'));
const CartScreen = lazy(() => import('./components/Market/CartScreen'));
const CheckoutScreen = lazy(() => import('./components/Market/CheckoutScreen'));
const PaymentDetailScreen = lazy(() => import('./components/Market/PaymentDetailScreen'));
const QuizHubScreen = lazy(() => import('./components/Belajar/QuizHubScreen'));
const PrayerJournalScreen = lazy(() => import('./components/JurnalDoa/PrayerJournalScreen'));
const KhutbahLibraryScreen = lazy(() => import('./components/Khutbah/KhutbahLibraryScreen'));
const HafalanScreen = lazy(() => import('./components/Hafalan/HafalanScreen'));
import { CartProvider } from './components/Market/CartContext';

// Import shared components
import { LoadingSpinner } from './components/shared/Common';
import NotFoundScreen from './components/shared/NotFoundScreen';
import ErrorBoundary from './components/shared/ErrorBoundary';
import PWAInstallPrompt from './components/shared/PWAInstallPrompt';
import OfflineBanner from './components/shared/OfflineBanner';
import Toast from './components/shared/Toast';
import { fetchMergedAppConfig } from './utils/appConfigLoad';
import { mapConfigLoadError, messageForConfigError } from './utils/appErrors';
import { captureEvent } from './posthogClient';
import { IconHome, IconNewspaper, IconUser } from './shared/Icons'; // IconCash dihapus karena tidak lagi dipakai di BottomNav
// Default config in case fetching fails or for local development
const DEFAULT_CONFIG = {
  appName: "Mosqify",
  defaultApiBaseUrl: "https://equran.id/api/v2",
  featureFlags: {
    showDonasi: true,
    showMarket: false, // Ensure market is hidden
    /** F18: sticky offline banner */
    pwaOfflineUx: true,
    /** F19: typed config errors + toast + PostHog app_error_shown */
    pwaErrorTaxonomy: false,
    /** F20: extra install funnel (e.g. pwa_install_click) */
    pwaInstallAnalytics: false,
    /** F22–F24 Islamic learning (see MOSQIFY-PWA-IMPROVEMENTS PRD); F21 hafalan is always on Al Quran */
    quizHub: false,
    prayerJournal: false,
    khutbahLibrary: false,
  },
  contactEmail: "default@examplemasjid.com",
  prayerTimesConfig: { // Added default prayerTimesConfig
    city: "Jakarta",    // Default city
    country: "Indonesia", // Default country
    method: 20,          // Default method (Kemenag RI)
    apiBaseUrl: "https://api.aladhan.com/v1" // Default API
  }
};
//================================================================
// AppContent component to house logic requiring CartProvider and AuthContext
//================================================================
const AppContent = () => {
  const { isLoading: isAuthLoading } = useAuth();
  const posthog = usePostHog();
  const [appConfig, setAppConfig] = useState(DEFAULT_CONFIG);
  const [configLoading, setConfigLoading] = useState(true);
  const [configToastOpen, setConfigToastOpen] = useState(false);
  const [configErrorCode, setConfigErrorCode] = useState(null);
  const [runtimeToast, setRuntimeToast] = useState({ open: false, message: '', code: '' });
  const configErrorToastShownRef = useRef(false);
  const location = useLocation();

  // Guarantee one event for PostHog "Verify Installation" / Live events
  useEffect(() => {
    if (posthog) {
      posthog.capture('pwa_app_loaded', {
        url: window.location.href,
        path: window.location.pathname,
        source: 'jemaah',
      });
    }
  }, [posthog]);

  const loadRemoteConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const merged = await fetchMergedAppConfig(DEFAULT_CONFIG);
      setAppConfig(merged);
      setConfigToastOpen(false);
      setConfigErrorCode(null);
      configErrorToastShownRef.current = false;
    } catch (caught) {
      const err = caught?.isAppError ? caught : mapConfigLoadError(caught, null);
      setAppConfig(DEFAULT_CONFIG);
      if (DEFAULT_CONFIG.featureFlags.pwaErrorTaxonomy && !configErrorToastShownRef.current) {
        configErrorToastShownRef.current = true;
        captureEvent('app_error_shown', { code: err.code, context: 'config_load' });
        setConfigErrorCode(err.code);
        setConfigToastOpen(true);
      }
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRemoteConfig();
  }, [loadRemoteConfig]);

  useEffect(() => {
    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      if (!reason?.isApiError) return;
      const code = reason.code || 'UNKNOWN';
      let message = reason.message || 'Terjadi kesalahan jaringan.';
      if (code === 'NETWORK_OFFLINE') message = 'Tidak ada koneksi internet.';
      if (code === 'NETWORK_TIMEOUT') message = 'Permintaan terlalu lama (timeout).';
      if (code === 'NETWORK_FETCH_FAILED') message = 'Koneksi ke server gagal.';
      setRuntimeToast({ open: true, message, code });
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', onUnhandledRejection);
  }, []);

  if (configLoading || isAuthLoading) {
    return <LoadingSpinner message={configLoading ? "Memuat konfigurasi..." : "Bismillah menyiapkan sesi Anda..."} />;
  }

  // Allow guest access - PWA is for users, login is optional
  // JANGAN redirect ke login, biarkan user melihat konten dengan UI login prompt

  // User is authenticated (non-anonymous), or an anonymous user accessing non-protected paths, or on the login page.
  const offlineEnabled = Boolean(appConfig.featureFlags.pwaOfflineUx);
  const installAnalytics = Boolean(appConfig.featureFlags.pwaInstallAnalytics);
  const configToastMessage = messageForConfigError(configErrorCode || 'UNKNOWN');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-sans">
      {/* WCAG: Skip link for keyboard users */}
      <a href="#main-content" className="skip-link">
        Lewati ke konten utama
      </a>
      <OfflineBanner enabled={offlineEnabled} onRetry={() => window.location.reload()} />
      {DEFAULT_CONFIG.featureFlags.pwaErrorTaxonomy && configToastOpen && (
        <Toast
          type="warning"
          message={configToastMessage}
          isVisible={configToastOpen}
          duration={0}
          onClose={() => setConfigToastOpen(false)}
          actionLabel="Coba lagi"
          onAction={() => {
            configErrorToastShownRef.current = false;
            loadRemoteConfig();
          }}
        />
      )}
      {runtimeToast.open && (
        <Toast
          type="error"
          message={runtimeToast.message}
          isVisible={runtimeToast.open}
          duration={5000}
          onClose={() => setRuntimeToast({ open: false, message: '', code: '' })}
          actionLabel="Muat ulang"
          onAction={() => window.location.reload()}
        />
      )}
      <main id="main-content" className="pb-20">
        <Suspense fallback={<LoadingSpinner message="Memuat halaman..." />}>
        <Routes>
          {/* Login Route - does not show BottomNav */}
          <Route path="/login" element={<MosqifyApiLoginScreen />} />

          {/* Auth Pages - accessible without login */}
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/verify-email" element={<EmailVerificationScreen />} />
          {process.env.NODE_ENV === 'development' && (
            <Route path="/test-email-verification" element={<TestEmailVerification />} />
          )}
          <Route path="/auth/callback/google" element={<GoogleOAuthCallback />} />

          {/* Legal Pages - accessible without login */}
          <Route path="/terms-of-service" element={<TermsOfServiceScreen />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />

          {/* Root Route - redirect to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* QR Deep link - mosque profile + Daily Jamaah check-in */}
          <Route path="/m/:mosqueId" element={<MosqueProfileFromQR appConfig={appConfig} />} />

          {/* Authenticated/Guest Routes - show BottomNav */}
          <Route path="/beranda" element={<BerandaScreen appConfig={appConfig} />} />
          <Route path="/misi-harian" element={<MisiHarianScreen />} />
          <Route path="/al-quran" element={<QuranFeature appConfig={appConfig} />} />
          <Route path="/berita/*" element={<BeritaScreen appConfig={appConfig} />} />
          <Route path="/kalkulator-zakat/*" element={<ZakatCalculatorScreen appConfig={appConfig} />} />
          <Route path="/zikir" element={<ZikirScreen />} />
          <Route path="/doa" element={<DoaFeature appConfig={appConfig} />} />
          <Route path="/kibla" element={<KiblaScreen />} />
          <Route path="/jadwal-solat" element={<JadwalSolatScreen appConfig={appConfig} />} />

          {/* Profile Routes - Bisa diakses semua, tapi akan show login prompt jika belum login */}
          <Route path="/profile" element={<ProfileScreen appConfig={appConfig} />} />
          <Route path="/profile/info" element={<AccountInfoScreen appConfig={appConfig} />} />
          <Route path="/profile/inbox" element={<InboxScreen appConfig={appConfig} />} />
          <Route path="/profile/donations" element={<DonationHistoryScreen appConfig={appConfig} />} />
          <Route path="/profile/settings" element={<SettingsScreen appConfig={appConfig} />} />
          <Route path="/profile/help" element={<HelpCenterScreen appConfig={appConfig} />} />
          <Route path="/profile/shipping-status" element={<ShippingStatusScreen appConfig={appConfig} />} />

          {appConfig.featureFlags.showDonasi && (
            <Route path="/donasi/*" element={<DonasiScreen appConfig={appConfig} />} />
          )}

          {/* Protected routes - Bisa diakses semua, tapi akan show login prompt jika belum login */}
          <Route path="/cash-flow" element={<CashFlowScreen appConfig={appConfig} />} />
          <Route path="/aktifitas" element={<AktifitasScreen appConfig={appConfig} />} />
          <Route path="/donatur" element={<DonaturScreen appConfig={appConfig} />} />

          {appConfig.featureFlags.showMarket && (
            <>
              <Route path="/market" element={<MarketScreen appConfig={appConfig} />} />
              <Route path="/market/cart" element={<CartScreen />} />
              <Route path="/market/checkout" element={<CheckoutScreen />} />
              <Route path="/market/payment-detail" element={<PaymentDetailScreen />} />
            </>
          )}

          <Route path="/events" element={<EventScreen />} />
          <Route path="/events/my-tickets" element={<TicketHistoryScreen />} />
          <Route path="/events/:id/register" element={<EventRegistrationScreen />} />
          <Route path="/events/:id/pay" element={<EventPaymentScreen />} />
          <Route path="/events/:id" element={<EventDetailScreen />} />
          <Route path="/cari-masjid" element={<PetaMasjid />} />
          <Route path="/menu-lainnya" element={<LainnyaMenuScreen appConfig={appConfig} />} />

          {appConfig.featureFlags.quizHub && (
            <Route path="/belajar" element={<QuizHubScreen />} />
          )}
          {appConfig.featureFlags.prayerJournal && (
            <Route path="/jurnal-doa" element={<PrayerJournalScreen />} />
          )}
          {appConfig.featureFlags.khutbahLibrary && (
            <>
              <Route path="/khutbah" element={<KhutbahLibraryScreen />} />
              <Route path="/khutbah/:id" element={<KhutbahLibraryScreen />} />
            </>
          )}
          <Route path="/hafalan" element={<HafalanScreen />} />
          
          {/* 404 - Islamic themed, no redirect */}
          <Route path="/404" element={<NotFoundScreen />} />
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
        </Suspense>
      </main>
      {/* Conditionally render BottomNav: not on /login, /forgot-password, /verify-email, /test-email-verification, /auth/callback, or legal pages */}
      {location.pathname !== '/login' && 
       location.pathname !== '/forgot-password' &&
       location.pathname !== '/verify-email' &&
       location.pathname !== '/test-email-verification' &&
       !location.pathname.startsWith('/auth/callback') &&
       location.pathname !== '/terms-of-service' &&
       location.pathname !== '/privacy-policy' && 
       <BottomNav />}
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt analyticsEnabled={installAnalytics} />
    </div>
  );
};

//================================================================
// MAIN PWA SHELL COMPONENT
//================================================================
const App = () => {
  // The Router (HashRouter) is now expected to be in src/index.js, wrapping this App component.
  // So, we remove the Router component from here.
  const location = useLocation();

  // Mosque TV screen: public, full screen, no auth/config/nav shell.
  if (location.pathname.startsWith('/tv/')) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/tv/:mosqueId" element={<TvDisplayScreen />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <MosqueProvider>
            <CartProvider>
            <AppContent />
            <SpeedInsights /> {/* Vercel Speed Insights */}
            </CartProvider>
          </MosqueProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

//================================================================
// NAVIGATION COMPONENTS
//================================================================
const BottomNav = () => {
  const location = useLocation();
  const { user } = useAuth(); // Access user state to potentially change nav items

  // If there's no user (e.g. guest or not logged in), don't show profile or other protected nav items
  // This is an example; you might want to always show Profile and let the ProfileScreen handle redirection
  // For simplicity, we'll keep the nav items constant and let route protection handle access.
  const navItems = [
    { name: 'Beranda', path: '/beranda', icon: <IconHome /> },
    { name: 'Berita', path: '/berita', icon: <IconNewspaper /> },
    { name: 'Profil', path: '/profile', icon: <IconUser /> },
  ];

  // Do not render BottomNav if on the login page
  if (location.pathname === '/login') {
    return null;
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] dark:shadow-[0_-2px_10px_rgba(0,0,0,0.3)] max-w-md mx-auto rounded-t-2xl px-4">
      <ul className="flex justify-between items-center">
        {navItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path !== '/beranda' && item.path !== '/' && location.pathname.startsWith(item.path));

          return (
            <li key={item.name} className="flex-1">
              <Link
                to={item.path}
                className={`group flex flex-col items-center justify-center relative py-3 transition-all duration-200`}
              >
                <span
                  className={`absolute top-0 left-1/2 -translate-x-1/2 w-8 h-1 rounded-b-full transition-all duration-300 ease-in-out ${
                    isActive ? 'bg-green-600' : 'bg-transparent'
                  }`}
                />
                <div
                  className={`text-xl transition-transform duration-200 ease-in-out ${
                    isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-green-500 dark:group-hover:text-green-400'
                  }`}
                >
                  {React.cloneElement(item.icon, { className: 'w-6 h-6' })}
                </div>
                <span
                  className={`mt-1 text-xs transition-colors duration-200 ${
                    isActive ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-gray-500 dark:text-gray-400 group-hover:text-green-500 dark:group-hover:text-green-400'
                  }`}
                >
                  {item.name}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default App;
